import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { toast } from "sonner";
import { newsReactionsQueryOptions, toggleNewsReactionAction } from "@/lib/client/news-reaction";
import {
  NEWS_REACTION_EMOJIS,
  NewsReactionEmoji,
  getNewsReactionTargetKey,
} from "@/lib/utils/news-reaction";
import { NEWS_REACTION_PREVIEW_LIMIT } from "./constants";

type NewsReactionSummary = {
  emoji: NewsReactionEmoji;
  count: number;
  reacted: boolean;
};

type NewsReactionBucket = Map<NewsReactionEmoji, NewsReactionSummary>;

type ReactionItem = {
  targetType: "news" | "comment";
  targetId: string;
  emoji: NewsReactionEmoji;
  count: number;
  isReactedByCurrentUser: boolean;
};

function createEmptyNewsReactionState() {
  return new Map<NewsReactionEmoji, NewsReactionSummary>(
    NEWS_REACTION_EMOJIS.map((emoji) => [emoji, { emoji, count: 0, reacted: false }]),
  );
}

function mergeNewsReactionSummary(reactions: ReactionItem[]) {
  const buckets = new Map<string, NewsReactionBucket>();
  for (const reaction of reactions) {
    const key = getNewsReactionTargetKey(reaction);
    const bucket = buckets.get(key) ?? createEmptyNewsReactionState();
    const summary = bucket.get(reaction.emoji);
    if (summary) {
      bucket.set(reaction.emoji, {
        ...summary,
        count: reaction.count,
        reacted: reaction.isReactedByCurrentUser,
      });
    }
    buckets.set(key, bucket);
  }

  return buckets;
}

export function NewsReactionSection({
  articleId,
  currentUser,
}: {
  articleId: string;
  currentUser: { id: string } | null;
}) {
  const queryClient = useQueryClient();
  const { data: reactions = [] } = useSuspenseQuery(
    newsReactionsQueryOptions([{ targetType: "news", targetId: articleId }]),
  );
  const reactionMap = useMemo(() => mergeNewsReactionSummary(reactions), [reactions]);
  const key = getNewsReactionTargetKey({ targetType: "news", targetId: articleId });
  const bucket = reactionMap.get(key) ?? createEmptyNewsReactionState();

  const reactionMutation = useMutation({
    mutationFn: (emoji: NewsReactionEmoji) =>
      toggleNewsReactionAction({
        targetType: "news",
        targetId: articleId,
        emoji,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["news-reactions"] });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "reaction 失败，请稍后重试");
    },
  });

  return (
    <section className="px-1">
      <div className="mx-auto flex max-w-full flex-wrap justify-center gap-1">
        {NEWS_REACTION_EMOJIS.slice(0, NEWS_REACTION_PREVIEW_LIMIT).map((emoji) => {
          const summary = bucket.get(emoji) ?? { count: 0, reacted: false, emoji };
          const buttonText = `${summary.count}`;

          return (
            <button
              key={`${key}:${emoji}`}
              type="button"
              disabled={!currentUser || reactionMutation.isPending}
              className={`inline-flex min-h-7 shrink-0 items-center gap-1 rounded-full border px-2 text-xs ${
                summary.reacted
                  ? "border-rose-500 bg-rose-500/10 text-rose-600"
                  : "border-border/80 text-muted-foreground"
              }`}
              onClick={() => {
                if (!currentUser) {
                  toast.error("请先登录后再进行 reaction");
                  return;
                }
                reactionMutation.mutate(emoji);
              }}
            >
              <span>{emoji}</span>
              <span>{buttonText}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
